import React from "react"
import {useTranslation} from "react-i18next"
import {ChevronRightIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import {UnstyledLink} from "../../../../components/Link.tsx"
import {i18n} from "../../../../i18n.ts"
import {EFeature, EStep, featureToStep} from "./utils.ts"

const allFeatures = [EFeature.Leads, EFeature.XLS, EFeature.Single] satisfies EFeature[]

const featureConfig = {
  [EFeature.Single]: {
    title: i18n.t("Prospects_ImportModal_SingleStep_Button_Title"),
    subtitle: i18n.t("Prospects_ImportModal_SingleStep_Button_Subtitle"),
  },
  [EFeature.XLS]: {
    title: i18n.t("Prospects_ImportModal_UploadStep_Button_Title"),
    subtitle: i18n.t("Prospects_ImportModal_UploadStep_Button_Subtitle"),
  },
  [EFeature.Leads]: {
    title: i18n.t("Prospects_ImportModal_LeadsStep_Button_Title"),
    subtitle: i18n.t("Prospects_ImportModal_LeadsStep_Button_Subtitle"),
  },
} satisfies {[feature in EFeature]: {title: string; subtitle: string}}

export const InitialStep: React.FC<{
  onStepPick: (step: EStep) => void
  onClose: () => void
  enabledFeatures: EFeature[]
}> = ({onStepPick, enabledFeatures}) => {
  const {t} = useTranslation()

  const allowedFeatures = React.useMemo(() => {
    return allFeatures.filter(feature => enabledFeatures.includes(feature))
  }, [enabledFeatures])

  return (
    <div className={"flex flex-col gap-8"}>
      <div className={"flex flex-col gap-3"}>
        <h1 className={"text-2xl font-semibold"}>{t("Prospects_ImportModal_InitialStep_Title")}</h1>
      </div>

      {allowedFeatures.length > 0 && (
        <div className={"flex flex-col gap-4"}>
          <div className={"flex flex-col gap-3"}>
            {allowedFeatures.map(feature => (
              <SourceButton
                key={feature}
                onSelect={() => onStepPick(featureToStep[feature])}
                title={featureConfig[feature].title}
                subtitle={featureConfig[feature].subtitle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const SourceButton: React.FC<{
  onSelect: () => void
  title: React.ReactNode
  subtitle: React.ReactNode
}> = ({onSelect, subtitle, title}) => {
  return (
    <UnstyledLink
      onClick={onSelect}
      className={twMerge(
        "group flex cursor-pointer gap-8 rounded-lg border border-cr-blue-mid-light bg-cr-blue-super-light px-6 py-4 transition-all hover:border-cr-indigo-mid"
      )}
    >
      <div className={"flex grow flex-col"}>
        <h3 className={"text-lg font-medium"}>{title}</h3>
        <span className={"font-light text-cr-black"}>{subtitle}</span>
      </div>
      <div
        className={"flex shrink-0 items-center justify-end text-cr-grey-65 transition-all group-hover:text-cr-black"}
      >
        <ChevronRightIcon className={"size-9"} />
      </div>
    </UnstyledLink>
  )
}
